import { zodResolver } from "@hookform/resolvers/zod";
import { UploadCloud, X } from "lucide-react";
import { ErrorCode } from "react-dropzone";
import { useForm, type UseFormReturn } from "react-hook-form";
import { z } from "zod";
import { FormItemWithMessage } from "@/components/FormItemWithMessage";
import { SelectInput, type SelectOption } from "@/components/SelectInput";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Dropzone, DropzoneDescription, DropzoneInput, DropzoneTitle, DropzoneZone } from "@/components/ui/dropzone";
import {
  FileList,
  FileListAction,
  FileListDescription,
  FileListHeader,
  FileListIcon,
  FileListInfo,
  FileListItem,
  FileListName,
  FileListSize,
} from "@/components/ui/file-list";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { createRequiredStringSchema } from "@/lib/utils/validation/zod";

const MAX_FILE_SIZE = 5000000; // 5MB
const ACCEPTED_FILE_TYPES = ["image/jpeg", "image/jpg", "image/png", "application/pdf", "text/csv"];

export const documentUploadFormSchema = z.object({
  name: createRequiredStringSchema("document name", 50),
  type: createRequiredStringSchema("document type", 50),
  file: z
    .instanceof(File)
    .refine((file) => file.size <= MAX_FILE_SIZE, "File exceed max file size")
    .refine(
      (file) => ACCEPTED_FILE_TYPES.includes(file.type),
      "Only .jpg, .jpeg, .png, .pdf and .csv files are accepted",
    )
    .optional()
    .superRefine((file, ctx) => {
      if (!file) {
        ctx.addIssue({
          code: "custom",
          message: "File is required",
        });
      }
    }),
});

interface DocumentUploadFormProps {
  onSubmit: (values: z.infer<typeof documentUploadFormSchema>) => Promise<void>;
  onCancel: () => void;
  documentTypeOptions: SelectOption[];
  isLoadingDocumentTypes?: boolean;
}

export function DocumentUploadForm({
  onSubmit,
  onCancel,
  documentTypeOptions,
  isLoadingDocumentTypes = false,
}: DocumentUploadFormProps) {
  const form = useForm<z.infer<typeof documentUploadFormSchema>>({
    resolver: zodResolver(documentUploadFormSchema),
    defaultValues: {
      name: "",
      type: "",
      file: undefined,
    },
  });

  const currentFile = form.watch("file");

  const handleSubmit = form.handleSubmit((data) => {
    return onSubmit(data);
  });

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit}>
        {/* Hidden submit button catches Enter key */}
        <Button
          isLoading={form.formState.isSubmitting}
          disabled={form.formState.isSubmitting}
          type="submit"
          variant="confirm"
          className="hidden"
        >
          Submit
        </Button>
        <div className="md:flex md:gap-4 md:space-y-0">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItemWithMessage
                label="Document Name"
                inputComponent={<Input placeholder="Enter document name" {...field} />}
                className="md:flex-1"
              />
            )}
          />

          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItemWithMessage
                label="Type *"
                inputComponent={
                  <SelectInput
                    loading={isLoadingDocumentTypes}
                    placeholder="Choose a type"
                    options={documentTypeOptions}
                    {...field}
                  />
                }
                className="md:flex-1"
              />
            )}
          />
        </div>

        {currentFile ? (
          <SelectedFileDisplay
            file={currentFile}
            onRemove={() => form.setValue("file", undefined)}
            error={form.formState.errors.file?.message}
          />
        ) : (
          <FileUploadZone form={form} />
        )}

        <DialogFooter className="gap-y-2 mt-2 md:mt-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onCancel();
              form.reset();
            }}
          >
            Cancel
          </Button>
          <Button
            isLoading={form.formState.isSubmitting}
            disabled={form.formState.isSubmitting}
            type="submit"
            variant="confirm"
          >
            Save and Upload
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

function SelectedFileDisplay({ file, onRemove, error }: { file: File; onRemove: () => void; error?: string }) {
  return (
    <div className="grid gap-4">
      <h6 className="font-semibold leading-none tracking-tight">Selected File</h6>
      <FileList>
        <FileListItem>
          <FileListHeader>
            <FileListIcon />
            <FileListInfo>
              <FileListName>{file.name}</FileListName>
              <FileListDescription>
                <FileListSize>{file.size}</FileListSize>
              </FileListDescription>
            </FileListInfo>
            <FileListAction onClick={onRemove}>
              <X />
              <span className="sr-only">Remove</span>
            </FileListAction>
          </FileListHeader>
        </FileListItem>
      </FileList>
      <div className="min-h-[20px]">{error && <FormMessage>{error}</FormMessage>}</div>
    </div>
  );
}

function FileUploadZone({ form }: { form: UseFormReturn<z.infer<typeof documentUploadFormSchema>> }) {
  const { toast } = useToast();

  return (
    <Dropzone
      maxSize={MAX_FILE_SIZE}
      onDropAccepted={async (acceptedFiles) => {
        const file = acceptedFiles[0];
        if (file) {
          if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
            form.setError("file", {
              message:
                file.type.length > 0 ? `Invalid file type. ${file.type} is not supported.` : "Invalid file type.",
            });
            return;
          }

          form.setValue("file", file);
          await form.trigger("file");
        } else {
          toast({
            variant: "destructive",
            title: "No file found.",
            description: "Please select a file to upload.",
          });
        }
      }}
      onDropRejected={(fileRejections) => {
        fileRejections.forEach((fileRejection) => {
          if (fileRejection.errors.some((err) => err.code === ErrorCode.FileTooLarge)) {
            form.setError("file", {
              message: `File '${fileRejection.file.name}' is too large. Must be less than 5MB`,
            });
            return;
          }
          toast({
            variant: "destructive",
            title: "Upload failed",
            description: "An unexpected error occurred while uploading the file. Please try again.",
          });
        });
      }}
    >
      <FormField
        control={form.control}
        name="file"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="sr-only">File</FormLabel>
            <DropzoneZone className="flex justify-center bg-muted-extralight hover:bg-muted-light hover:border-accent-foreground/50 transition-colors">
              <FormControl>
                <DropzoneInput disabled={field.disabled} name={field.name} onBlur={field.onBlur} ref={field.ref} />
              </FormControl>
              <div className="flex items-center gap-6">
                <div className="flex flex-col items-center gap-2">
                  <UploadCloud className="h-5 w-5" />
                  <DropzoneTitle className="text-sm">Drag and drop your file here or click to browse</DropzoneTitle>
                  <DropzoneDescription className="text-xs">
                    Supported file types: CSV, PNG, JPG, PDF
                  </DropzoneDescription>
                </div>
              </div>
            </DropzoneZone>
            <div className="min-h-[20px]">
              <FormMessage />
            </div>
          </FormItem>
        )}
      />
    </Dropzone>
  );
}
